import { create } from 'zustand'
import * as Network from 'expo-network'
import type * as Sentry from '@sentry/react-native'

const networkStatePromise = Network.getNetworkStateAsync()

const useNetworkState = create<typeof networkStatePromise | Awaited<typeof networkStatePromise>>(
  (set) => (
    networkStatePromise.then(set),
    Network.addNetworkStateListener(set),
    networkStatePromise
  ),
)

const NETWORK_ERROR_MESSAGES = [
  'Network request failed', // React Native fetch error
  'Failed to fetch', // Browser fetch error (also used by Apollo)
  'Load failed', // iOS/WebKit fetch error (Chrome on iOS uses WebKit)
] as const satisfies string[]

/**
 * Checks if an error is a network request failure due to unreachable internet.
 * Handles both RTK Query and Apollo Client network errors across platforms.
 */
export function isNetworkRequestFailedDueToUnreachableInternet({
  value,
}: Sentry.Exception): boolean {
  if (!NETWORK_ERROR_MESSAGES.some((message) => message === value)) {
    return false
  }

  const networkState = useNetworkState.getState()

  // If still initializing (promise), don't suppress
  if (networkState instanceof Promise) {
    return false
  }

  // Only suppress if internet is explicitly unreachable (false)
  // If null (not initialized) or true, don't suppress
  return networkState.isInternetReachable === false
}
